import React, { FC, ReactNode, useCallback } from 'react';
import {
  FieldErrors,
  type FieldValues,
  FormProvider,
  type SubmitErrorHandler,
  type SubmitHandler,
  useFormContext,
  type UseFormReturn,
  useWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, type BoxProps, Button, Divider, Stack, type SxProps, type Theme, useTheme } from '@mui/material';
import { mergeSx } from 'merge-sx';

import LoadingButton from '@/components/LoadingButton';

type FormSubmitHandler<TFieldValues extends FieldValues, TTransformedValues> = TTransformedValues extends undefined
  ? SubmitHandler<TFieldValues>
  : TTransformedValues extends FieldValues
    ? SubmitHandler<TTransformedValues>
    : never;

interface FormProps<TFieldValues extends FieldValues, TContext, TTransformedValues extends undefined> {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  onSubmit?: FormSubmitHandler<TFieldValues, TTransformedValues>;
  onError?: SubmitErrorHandler<TFieldValues>;
  children?: ReactNode;
}

const Form = <TFieldValues extends FieldValues, TContext, TTransformedValues extends undefined>({
  form,
  onSubmit,
  onError,
  ...rest
}: FormProps<TFieldValues, TContext, TTransformedValues> & Omit<BoxProps, 'onSubmit'>) => {
  const { handleSubmit, getValues } = form || {};

  const handleError = useCallback(
    (errors: FieldErrors<TFieldValues>) => {
      // eslint-disable-next-line i18next/no-literal-string, no-console
      console.warn('Validation errors', errors);
      // eslint-disable-next-line i18next/no-literal-string, no-console
      console.warn('Form values', getValues());
      onError?.(errors);
    },
    [getValues, onError]
  );
  // TODO: fix non-null assertion on optional prop
  const handleFormSubmit = useCallback(
    (event: React.BaseSyntheticEvent) => {
      event.stopPropagation();

      return handleSubmit(onSubmit!, handleError)(event);
    },
    [handleSubmit, onSubmit, handleError]
  );

  return (
    <FormProvider {...form}>
      <Box component="form" onSubmit={handleFormSubmit} {...rest} />
    </FormProvider>
  );
};

export default Form;

interface CommonFormFooterProps {
  sx?: SxProps<Theme>;
  onReset?: () => void;
  successText?: ReactNode;
  submitText?: ReactNode;
  resetText?: ReactNode;
  buttonsContainerSx?: SxProps;
  showResetButton?: boolean;
  disableSubmit?: boolean;
}
export const CommonFormFooter = ({
  sx,
  onReset,
  successText,
  submitText,
  resetText,
  buttonsContainerSx,
  showResetButton = true,
  disableSubmit = false
}: CommonFormFooterProps) => {
  const { t } = useTranslation('settings');
  const { reset, formState } = useFormContext();
  const { palette, spacing } = useTheme();
  const hasDirtyFields = !!Object.keys(formState.dirtyFields).length;

  return (
    <Box sx={sx} className="CommonFormFooter-root">
      <Divider sx={{ borderColor: palette.grey[400] }} />
      <Stack
        direction="row"
        sx={mergeSx(
          { padding: spacing(2.5, 2, 2), gap: spacing(1.5), justifyContent: 'space-between' },
          buttonsContainerSx
        )}
      >
        {showResetButton && (
          <Button onClick={onReset ? onReset : () => reset()} disabled={!hasDirtyFields}>
            {resetText ?? t('settings:settings.resetButton')}
          </Button>
        )}
        <LoadingButton
          type="submit"
          color="primary"
          autoFocus
          variant="contained"
          disabled={!hasDirtyFields || disableSubmit}
          isLoading={formState.isSubmitting}
        >
          {formState.isSubmitSuccessful && !hasDirtyFields
            ? successText ?? t('settings:settings.savedButton')
            : submitText ?? t('settings:settings.saveButton')}
        </LoadingButton>
      </Stack>
    </Box>
  );
};

export const FormLoader: FC<{ children?: ReactNode; fallback?: ReactNode }> = ({ children, fallback = null }) => {
  const { control } = useFormContext();

  const values = useWatch({ control });

  if (Object.keys(values).length === 0) {
    return fallback;
  }

  return children;
};
